import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const closeAll = () => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className={`text-xl md:text-2xl font-heading font-bold whitespace-nowrap transition-colors duration-300 ${
                isScrolled ? 'text-foreground-900' : 'text-white'
              }`}
              onClick={closeAll}
            >
              Hipobuy Spread
            </Link>

            <div className="hidden md:flex items-center gap-4">
              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-label font-medium whitespace-nowrap cursor-pointer transition-colors duration-300 ${
                    isScrolled
                      ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <span>Categories</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-[720px] bg-background-50 rounded-lg overflow-hidden z-50"
                    style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
                  >
                    <div className="grid grid-cols-4 gap-1 p-3">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-body text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors whitespace-nowrap"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className={`${cat.icon} text-primary-500 text-sm`}></i>
                          </span>
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-label whitespace-nowrap cursor-pointer transition-colors duration-300 ${
                isScrolled ? 'text-foreground-600 hover:text-foreground-900' : 'text-white/80 hover:text-white'
              }`}
            >
              Hipobuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 bg-white text-foreground-900 text-sm font-label font-semibold rounded-md whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line ml-1.5 text-xs"></i>
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-md cursor-pointer transition-colors ${
              isScrolled ? 'text-foreground-900 hover:bg-background-100' : 'text-white hover:bg-white/10'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-lg ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      <div
        className={`md:hidden fixed inset-0 top-16 z-40 bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeAll}
      ></div>

      <div
        className={`md:hidden fixed top-16 right-0 h-[calc(100vh-4rem)] w-[85vw] max-w-[380px] bg-background-50 z-50 transform transition-transform duration-300 ease-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ boxShadow: '-4px 0 24px rgba(0,0,0,0.08)' }}
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto py-4 px-4">
            <div className="space-y-4 mb-6">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-5 py-3.5 bg-foreground-900 text-white text-sm font-label font-semibold rounded-lg hover:bg-foreground-800 transition-colors"
                onClick={closeAll}
              >
                <i className="ri-external-link-line"></i>
                Visit Hipobuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-primary-500 text-white text-sm font-label font-semibold rounded-lg hover:bg-primary-600 transition-colors"
                onClick={closeAll}
              >
                <i className="ri-shopping-bag-line"></i>
                Buy It Now
              </a>
            </div>

            <div className="space-y-1 mb-6">
              <Link
                to="/"
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-label font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors"
                onClick={closeAll}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-home-line text-foreground-500"></i>
                </span>
                Home
              </Link>
              <Link
                to="/article/hipobuy-spreadsheet-guide"
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-label font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors"
                onClick={closeAll}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-book-open-line text-foreground-500"></i>
                </span>
                Complete Guide
              </Link>
              <Link
                to="/about"
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-label font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors"
                onClick={closeAll}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-information-line text-foreground-500"></i>
                </span>
                About
              </Link>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-2 px-2 mb-3">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-price-tag-3-line text-accent-500 text-xs"></i>
                </span>
                <p className="text-sm font-label font-bold text-foreground-900">All Categories</p>
                <span className="text-xs font-label bg-accent-100/80 text-accent-700 px-2 py-0.5 rounded-full">{categories.length}</span>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 px-3 py-3 rounded-lg text-sm font-body text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors active:scale-[0.98]"
                    onClick={closeAll}
                  >
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100/80 flex-shrink-0">
                      <i className={`${cat.icon} text-primary-600 text-xs`}></i>
                    </span>
                    <span className="truncate">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 p-4 border-t border-background-200/70 bg-background-100/50">
            <p className="text-center text-xs font-body text-foreground-500">
              Updated Monthly &middot; Safe &amp; Private
            </p>
          </div>
        </div>
      </div>
    </nav>
  );
}